import { Actor } from "../Actor";
import { Prop } from "../Prop";

export function compactSearchableStrings(
  ...values: Array<
    | string
    | number
    | boolean
    | null
    | undefined
    | Array<string | number | boolean | null | undefined>
  >
): string[] {
  const flattened = values.flat();
  const uniqueValues = new Set<string>();

  flattened.forEach((value) => {
    if (value === null || value === undefined) {
      return;
    }

    const normalizedValue = String(value).trim();
    if (!normalizedValue) {
      return;
    }

    uniqueValues.add(normalizedValue);
  });

  return Array.from(uniqueValues);
}

export type SearchLookupContext = {
  getActorSearchTerms: (actorId: string) => string[];
  getPropSearchTerms: (propId: string) => string[];
  getObjectSearchTerms: (objectId: string) => string[];
};

export function createSearchLookupContext(
  actors: Actor[],
  props: Prop[],
): SearchLookupContext {
  const actorsById = new Map(actors.map((actor) => [actor.id, actor]));
  const propsById = new Map(props.map((prop) => [prop.id, prop]));

  const getActorSearchTerms = (actorId: string): string[] => {
    const actor = actorsById.get(actorId);
    return compactSearchableStrings(actorId, actor?.workingName);
  };

  const getPropSearchTerms = (propId: string): string[] => {
    const prop = propsById.get(propId);
    return compactSearchableStrings(propId, prop?.name);
  };

  const getObjectSearchTerms = (objectId: string): string[] =>
    compactSearchableStrings(
      getActorSearchTerms(objectId),
      getPropSearchTerms(objectId),
    );

  return {
    getActorSearchTerms,
    getPropSearchTerms,
    getObjectSearchTerms,
  };
}

export function getBooleanSearchLabels(
  value: boolean,
  enabledLabel: string,
  disabledLabel: string,
): string[] {
  return value ? [enabledLabel, "true"] : [disabledLabel, "false"];
}
